import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const FAQPage = () => {
  const [openQuestion, setOpenQuestion] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/faqs`);
      if (response.ok) {
        const data = await response.json();
        setFaqs(data.faqs || []);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group FAQs by category
  const faqCategories = faqs.reduce((acc, faq) => {
    const existingCategory = acc.find(cat => cat.title === faq.category);
    if (existingCategory) {
      existingCategory.questions.push({
        id: faq.id,
        question: faq.question,
        answer: faq.answer
      });
    } else {
      acc.push({
        title: faq.category,
        icon: faq.category.includes('Getting') ? '🌊' : faq.category.includes('Booking') ? '📅' : faq.category.includes('Safety') ? '🛡️' : '❓',
        questions: [{
          id: faq.id,
          question: faq.question,
          answer: faq.answer
        }]
      });
    }
    return acc;
  }, []);

  const staticFaqCategories = [
    {
      title: 'Getting Started',
      icon: '🌊',
      questions: [
        {
          question: 'Do I need to know how to swim?',
          answer: 'Yes, basic swimming ability is required for all diving activities. You should be comfortable in water and able to swim at least 200 meters. For diving experiences, you\'ll be wearing a buoyancy control device (BCD) that helps you float, but swimming skills are essential for safety.'
        },
        {
          question: 'What if I have no diving experience?',
          answer: 'Perfect! Our Dive Exhibition experience is specifically designed for complete beginners. You\'ll receive a comprehensive safety briefing, learn basic hand signals, and experience diving in shallow, controlled conditions. No certification needed - just bring your enthusiasm!'
        },
        {
          question: 'What should I bring?',
          answer: 'Just bring swimwear, a towel, sun protection (hat, sunscreen, sunglasses), comfortable clothes for before/after, a water bottle, and your sense of adventure! All diving equipment, camping gear, and meals are provided.'
        },
        {
          question: 'What age restrictions apply?',
          answer: 'Minimum age for diving activities is 12 years old. For camping adventures, children 8+ can participate with a parent/guardian. All minors require parental consent and supervision. We\'d be happy to discuss family-friendly options!'
        }
      ]
    },
    {
      title: 'Safety & Equipment',
      icon: '🛡️',
      questions: [
        {
          question: 'How safe are your adventures?',
          answer: 'Safety is our absolute priority. Ahmed is PADI MSDT certified with 8+ years of experience, first aid certified, and maintains a 100% safety record. All equipment is professionally maintained and inspected. We never compromise on safety for the sake of adventure.'
        },
        {
          question: 'What equipment do you provide?',
          answer: 'Everything! For diving: wetsuits, masks, fins, BCDs, regulators, air tanks, and weights. For camping: tents, sleeping equipment, cooking gear, chairs, tables, and lighting. You just need to bring personal items like swimwear and clothes.'
        },
        {
          question: 'What happens if equipment doesn\'t fit?',
          answer: 'We carry a range of sizes for all equipment and do proper fitting before every activity. If something doesn\'t fit perfectly, we have backup options. Proper equipment fit is crucial for safety and comfort, so we take this very seriously.'
        },
        {
          question: 'Is all your gear sanitized?',
          answer: 'Absolutely! All equipment is thoroughly cleaned and sanitized between uses. Regulators and masks receive special attention with proper disinfection procedures. We follow strict hygiene protocols, especially post-COVID.'
        }
      ]
    },
    {
      title: 'Weather & Conditions',
      icon: '🌤️',
      questions: [
        {
          question: 'What happens in bad weather?',
          answer: 'Safety comes first! If weather conditions are unsafe for diving or camping, we\'ll reschedule your adventure at no cost or provide a full refund. We monitor weather conditions closely and will contact you 24-48 hours before your adventure if changes are needed.'
        },
        {
          question: 'What are the best conditions for diving?',
          answer: 'Ideal conditions include calm seas (under 1.5m swell), good visibility (10m+), and mild weather. However, we can safely dive in various conditions. We\'ll choose the best dive sites based on current conditions to ensure optimal safety and enjoyment.'
        },
        {
          question: 'Can we dive in winter?',
          answer: 'Yes! Winter diving in NSW can be amazing with excellent visibility and unique marine life. We provide high-quality thick wetsuits and thermal protection. Some of our most memorable dives happen in winter months!'
        },
        {
          question: 'Do you cancel often due to weather?',
          answer: 'Cancellations are rare (less than 10% of trips) because we\'re flexible with locations and timing. We have multiple backup dive sites and can adjust itineraries. Our local knowledge helps us find protected spots even when conditions are challenging elsewhere.'
        }
      ]
    },
    {
      title: 'Booking & Pricing',
      icon: '💳',
      questions: [
        {
          question: 'How far in advance should I book?',
          answer: 'We recommend booking 3-7 days in advance for best availability, especially for weekend trips. However, we can sometimes accommodate last-minute bookings (24-48 hours) depending on availability. Popular dates fill up quickly!'
        },
        {
          question: 'What\'s your cancellation policy?',
          answer: 'Cancellations 7+ days before receive a full refund. Cancellations 3-6 days before receive 50% refund. Cancellations within 48 hours are non-refundable. Weather-related cancellations always receive full refund or free rescheduling.'
        },
        {
          question: 'Are there group discounts?',
          answer: 'Our adventures are already small group experiences (max 4 people) at competitive prices. For repeat customers or special circumstances, contact Ahmed directly to discuss options. Quality and safety are our priorities over volume discounts.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards through our secure Stripe payment system, PayPal, bank transfers, and cash (for some experiences). Payment is required to secure your booking, with the option to pay deposits for multi-day adventures.'
        }
      ]
    },
    {
      title: 'Specific Adventures',
      icon: '🏊‍♂️',
      questions: [
        {
          question: 'What\'s included in camping adventures?',
          answer: 'Everything you need! Complete camping setup (tent, sleeping bag, pillow, mat), all meals and snacks, cooking equipment, campfire setup, drinking water, hot beverages, and professional guidance. You just bring personal clothing and toiletries.'
        },
        {
          question: 'Where are toilets during camping?',
          answer: 'We camp at established sites with toilet facilities nearby, or we choose locations where proper facilities are accessible within a short walk. Comfort and convenience are important parts of the adventure experience.'
        },
        {
          question: 'Can I bring my own equipment?',
          answer: 'Absolutely! If you have your own diving or camping gear, you\'re welcome to use it. However, it must meet safety standards, and diving equipment should be recently serviced. Using your own gear can sometimes reduce the trip cost - just ask!'
        },
        {
          question: 'What makes your adventures different?',
          answer: 'Our focus on mindful connection, small groups (max 4 people), Ahmed\'s personal attention as an MSDT instructor, combination of diving and camping, and emphasis on personal growth beyond just adventure. It\'s about transformation, not just tourism.'
        }
      ]
    },
    {
      title: 'Health & Accessibility',
      icon: '🏥',
      questions: [
        {
          question: 'Do I need a medical clearance?',
          answer: 'You\'ll complete a medical questionnaire before diving. If you have any medical conditions (heart, lung, ear issues, medications, etc.), you may need medical clearance from a dive-qualified doctor. This is standard PADI protocol for everyone\'s safety.'
        },
        {
          question: 'Can people with disabilities participate?',
          answer: 'We\'re happy to discuss accommodations for various abilities. Diving can be particularly therapeutic and accessible for many people with disabilities. Contact Ahmed directly to discuss your specific needs and how we can create an inclusive experience.'
        },
        {
          question: 'What if I get seasick?',
          answer: 'Most of our diving is shore-based, which eliminates seasickness! For boat dives (rare), we can provide recommendations for natural remedies and choose calmer conditions. Shore diving is perfect for those concerned about motion sickness.'
        },
        {
          question: 'Are your adventures suitable for seniors?',
          answer: 'Age is just a number! We\'ve guided adventurers well into their 70s. The key factors are reasonable fitness, comfort in water, and medical clearance if needed. Our small group approach means we can adapt the pace to suit everyone\'s abilities.'
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenQuestion(openQuestion === key ? null : key);
  };

  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600"></div>
      </div>
    );
  }

  // Use fetched FAQs if available, otherwise use static data
  const displayCategories = faqCategories.length > 0 ? faqCategories : staticFaqCategories;

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-base sm:text-lg opacity-90 max-w-3xl mx-auto">
              Everything you need to know about diving and camping adventures with Ahmed. 
              Can't find your answer? Just ask!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-4 sm:py-6 bg-coral-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <h3 className="text-base font-semibold">Still have questions?</h3>
              <p className="text-coral-100 text-sm">Ahmed responds to all inquiries within 24 hours</p>
            </div>
            <div className="flex space-x-3">
              <a
                href="https://wa.me/61414710541"
                className="bg-white text-coral-600 hover:bg-coral-50 px-4 py-2 rounded-full font-semibold transition-all text-sm"
              >
                💬 WhatsApp
              </a>
              <a
                href="/contact"
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-4 py-2 rounded-full font-semibold transition-all text-sm"
              >
                📧 Contact
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-10 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4 sm:space-y-6">
            {displayCategories.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
              >
                {/* Category Header */}
                <div className="bg-gradient-to-r from-slate-100 to-slate-200 p-3 sm:p-4">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{category.icon}</span>
                    <h2 className="text-lg sm:text-xl font-bold text-slate-800">{category.title}</h2>
                  </div>
                </div>

                {/* Questions */}
                <div className="p-3 sm:p-4">
                  <div className="space-y-2 sm:space-y-3">
                    {category.questions.map((faq, questionIndex) => {
                      const isOpen = openQuestion === `${categoryIndex}-${questionIndex}`;
                      return (
                        <div key={questionIndex} className="border border-slate-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                            className="w-full px-4 py-3 text-left bg-slate-50 hover:bg-slate-100 transition-colors flex justify-between items-center"
                          >
                            <span className="font-semibold text-slate-800 pr-3 text-sm sm:text-base">{faq.question}</span>
                            <span className={`text-coral-500 text-sm transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                              ▼
                            </span>
                          </button>
                          
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: 'auto' }}
                              exit={{ height: 0 }}
                              transition={{ duration: 0.3 }}
                              className="bg-white"
                            >
                              <div className="px-4 py-3 text-slate-600 leading-relaxed border-t border-slate-200 text-sm">
                                {faq.answer}
                              </div>
                            </motion.div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 bg-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-3xl shadow-lg border border-red-200">
            <div className="text-center">
              <div className="text-5xl mb-4">🚨</div>
              <h3 className="text-2xl font-bold text-red-800 mb-4">Emergency Information</h3>
              <div className="text-red-700 space-y-2">
                <p><strong>During Adventures:</strong> Ahmed carries emergency communication devices and first aid equipment</p>
                <p><strong>Emergency Contact:</strong> +61 400 000 000 (Available 24/7 during active trips)</p>
                <p><strong>Local Emergency Services:</strong> 000 (Police, Fire, Ambulance)</p>
                <p><strong>Marine Emergency:</strong> Marine Rescue NSW - Channel 16 VHF or 000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-coral-500 to-coral-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Your Adventure?</h2>
          <p className="text-xl text-coral-100 mb-8">
            Now that you have all the information, it's time to take the plunge and book your transformative experience!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/adventures"
              className="bg-white text-coral-600 hover:bg-coral-50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Browse Adventures
            </a>
            <a
              href="/book"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Book Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;